import type { TemplateSection } from 'inspection-types';

import { countSectionItems } from '../model/counts';
import { isInherited } from '../model/origin';
import { sortItems } from '../model/templateProjection';
import {
  createQuestionRowViewModel,
  type QuestionRowViewModel,
} from './createQuestionRowViewModel';
import { memoizeByNode } from './memoizeByNode';
import { LABEL_KEYS, type BadgeViewModel, type Message } from './messages';

export type SectionViewModel = {
  id: string;
  name: string;
  isHidden: boolean;
  isExpanded: boolean;
  badges: BadgeViewModel[];
  counts: Message;
  rows: QuestionRowViewModel[];
  emptyMessage: Message | null;
};

const createMemoizedRow = memoizeByNode(createQuestionRowViewModel);

function createCounts(section: TemplateSection): Message {
  const { visible, total, local } = countSectionItems(section);

  if (isInherited(section)) {
    return {
      key: LABEL_KEYS.sectionCountsInherited,
      params: { visible, total, local, count: local },
    };
  }

  return { key: LABEL_KEYS.sectionCountsLocal, params: { count: local } };
}

export function createSectionViewModel(
  section: TemplateSection,
  isExpanded: boolean
): SectionViewModel {
  const isHidden = Boolean(section.removed);
  const rows = isExpanded
    ? sortItems(section.items).map(item => createMemoizedRow(item, isHidden))
    : [];
  const isEmpty = isExpanded && section.items.length === 0;

  return {
    id: section.id,
    name: section.name,
    isHidden,
    isExpanded,
    badges: isInherited(section)
      ? [{ kind: 'lock', tooltipKey: LABEL_KEYS.lockSectionTooltip }]
      : [],
    counts: createCounts(section),
    rows,
    emptyMessage: isEmpty ? { key: LABEL_KEYS.noQuestions } : null,
  };
}
